import React, { Suspense } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const Card = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_.smart-card" */ '@atlaskit/smart-card').then(
		({ Card }) => ({
			default: Card,
		}),
	),
);

import { messages } from '../messages';
import type { PageSearchOutput, PageSearchPage } from '../types';

const styles = cssMap({
	card: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		marginBottom: token('space.100'),
	},
	itemNumber: {
		minWidth: '24px',
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
	snippet: {
		overflow: 'hidden',
		wordBreak: 'break-word',
	},
});

const PageCard = ({ page, index }: { page: PageSearchPage; index: number }) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.card}>
			<Stack space="space.100">
				<Inline space="space.100" alignBlock="start">
					<Box xcss={styles.itemNumber}>
						<Text weight="bold" color="color.text">
							{index + 1}.
						</Text>
					</Box>
					<Stack space="space.050">
						{page.url ? (
							<Suspense
								fallback={
									<Link href={page.url} target="_blank">
										{page.title}
									</Link>
								}
							>
								<Card appearance="inline" url={page.url} />
							</Suspense>
						) : (
							<Text weight="bold">{page.title}</Text>
						)}
						{page.space && (
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.ai_debugger_modal_page_search_space, {
									space: page.space,
								})}
							</Text>
						)}
					</Stack>
				</Inline>

				<Inline space="space.200">
					{page.author && (
						<Text size="small">
							{formatMessage(messages.ai_debugger_modal_author_label, {
								author: page.author,
							})}
						</Text>
					)}
					{page.date && (
						<Text size="small" color="color.text.subtlest">
							{page.date}
						</Text>
					)}
				</Inline>

				{page.snippet && (
					<Box xcss={styles.snippet}>
						<Text size="small" color="color.text.subtlest">
							{page.snippet}
						</Text>
					</Box>
				)}
			</Stack>
		</Box>
	);
};

export const PageSearchCard = ({ data }: { data: PageSearchOutput }): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.150">
			{data.header && <Text weight="medium">{data.header}</Text>}
			<Text size="small" color="color.text.subtlest">
				{formatMessage(messages.ai_debugger_modal_page_search_count, {
					count: data.pages.length,
				})}
			</Text>
			<Stack space="space.100">
				{data.pages.map((page, idx) => (
					<PageCard key={idx} page={page} index={idx} />
				))}
			</Stack>
		</Stack>
	);
};
